import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { CategoriesSelectors } from '@features/category';
import { isHomePage } from '@features/category/application/services/category.utils';
import { removeQueryParams } from '@frontend/common/utils';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { MenuCategory } from '@shared/header/models/category.model';

@Injectable()

/**
 * Servicio para la interacción con categorias
 */
export class MenuCategoriesService {
    constructor(
        private readonly store: Store,
    ) {}

    private readonly getMenuCategories$ = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getCategoriesNavigation$ = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    private readonly getMergedRoute$ = this.store.select(routerPublicQueries.getMergedRoute);

    /**
     * Función para obtener una categoría del arbol de Redux
     */
    public getCategory(categoryId: string): Observable<any[]> {
        return this.getMenuCategories$.pipe(
            map((categories: MenuCategory[]) => {
                return this.searchInTreeById(categories, categoryId);
            }),
            filter(category => !!category),
            map((category: MenuCategory) => {
                return category.subcategories
                    .filter((cat) => !cat.isMenuHidden)
                    .map((_category: MenuCategory) => {
                        return {
                            name: _category.name,
                            type: _category.isExternalUrl ? 'external' : 'category',
                            categoryId: !_category.isExternalUrl && _category.redirectToCategoryId
                                ? _category.redirectToCategoryId : _category.id,
                            src: _category.locationPath,
                        };
                    });
            })
        );
    }

    public getCurrentCategoryListeningUrl$ = this.getMergedRoute$.pipe(
        filter(data => !!data),
        map((data) => {
            return {
                view: data.data?.pageName || data.params?.view,
                url: data.url,
            };
        }),
        filter(({ view, url }) => !!view && !!url && !isHomePage(view)),
        switchMap(({ url }) => {
            return combineLatest([
                this.getMenuCategories$,
                this.getCategoriesNavigation$,
            ]).pipe(
                filter(([categories, categoriesNavigation]) => categories?.length > 0
                    && categoriesNavigation?.length > 0),
                first(),
                map(([categories, categoriesNavigation]) => {
                    const _url = removeQueryParams(url);
                    const category = categoriesNavigation
                        .find(cat => removeQueryParams(cat.locationPath).includes(_url));
                    return {
                        categories: categories,
                        category,
                    };
                })
            );
        }),
    );

    /**
     * Función para buscar recursivamente dentro del arbol de categorias en base a una Id
     */
    private searchInTreeById(categories: MenuCategory[], categoryId: string): MenuCategory {
        for (const category of categories || []) {
            if (category.id === categoryId) return category;

            const categroy_ = this.searchInTreeById(category.subcategories, categoryId);

            if (categroy_) return categroy_;
        }
    }
}
